import * as THREE from 'three';

// High-performance global in-memory texture cache
const textureCache = new Map();

/**
 * Noise helper for procedural planetary synthesis
 */
function pseudoNoise(x, y) {
  const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453123;
  return n - Math.floor(n);
}

function smoothNoise(x, y) {
  const i = Math.floor(x);
  const j = Math.floor(y);
  const fx = x - i;
  const fy = y - j;

  const a = pseudoNoise(i, j);
  const b = pseudoNoise(i + 1, j);
  const c = pseudoNoise(i, j + 1);
  const d = pseudoNoise(i + 1, j + 1);

  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);

  return a * (1 - ux) * (1 - uy) + b * ux * (1 - uy) + c * (1 - ux) * uy + d * ux * uy;
}

function fractalNoise(x, y, octaves = 5) {
  let v = 0;
  let amp = 0.5;
  let freq = 1;
  for (let o = 0; o < octaves; o++) {
    v += smoothNoise(x * freq, y * freq) * amp;
    amp *= 0.5;
    freq *= 2;
  }
  return v;
}

/**
 * 1. Earth-Like Habitable World Texture
 */
export function getEarthTexture() {
  if (textureCache.has('earth')) return textureCache.get('earth');

  const width = 1024;
  const height = 512;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const imgData = ctx.createImageData(width, height);
  const data = imgData.data;

  for (let y = 0; y < height; y++) {
    const lat = (y / height - 0.5) * Math.PI;
    const isPolar = Math.abs(lat) > 1.2;

    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const nx = x / 60;
      const ny = y / 60;
      const elevation = fractalNoise(nx, ny, 6);

      if (isPolar) {
        data[idx] = 230 + Math.random() * 25;
        data[idx + 1] = 240 + Math.random() * 15;
        data[idx + 2] = 255;
        data[idx + 3] = 255;
      } else if (elevation > 0.55) {
        const vegetation = fractalNoise(nx * 2, ny * 2, 4);
        if (vegetation > 0.5) {
          data[idx] = 34 + vegetation * 40;
          data[idx + 1] = 120 + vegetation * 70;
          data[idx + 2] = 60 + vegetation * 30;
        } else {
          data[idx] = 160 + vegetation * 50;
          data[idx + 1] = 130 + vegetation * 40;
          data[idx + 2] = 90 + vegetation * 20;
        }
        data[idx + 3] = 255;
      } else if (elevation > 0.5) {
        data[idx] = 20;
        data[idx + 1] = 120;
        data[idx + 2] = 190;
        data[idx + 3] = 255;
      } else {
        const depth = elevation / 0.5;
        data[idx] = 10 + depth * 15;
        data[idx + 1] = 30 + depth * 50;
        data[idx + 2] = 100 + depth * 90;
        data[idx + 3] = 255;
      }
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.minFilter = THREE.LinearFilter;
  textureCache.set('earth', texture);
  return texture;
}

/**
 * 2. Cloud Layer Texture
 */
export function getCloudTexture() {
  if (textureCache.has('clouds')) return textureCache.get('clouds');

  const width = 1024;
  const height = 512;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const imgData = ctx.createImageData(width, height);
  const data = imgData.data;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const nx = x / 45;
      const ny = y / 45;
      const density = fractalNoise(nx, ny, 5);

      if (density > 0.54) {
        const alpha = Math.min((density - 0.54) * 4.5 * 255, 230);
        data[idx] = 255;
        data[idx + 1] = 255;
        data[idx + 2] = 255;
        data[idx + 3] = alpha;
      } else {
        data[idx] = 0;
        data[idx + 1] = 0;
        data[idx + 2] = 0;
        data[idx + 3] = 0;
      }
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  textureCache.set('clouds', texture);
  return texture;
}

/**
 * 3. Mars / Desert Foundry World Texture
 */
export function getMarsTexture() {
  if (textureCache.has('mars')) return textureCache.get('mars');

  const width = 1024;
  const height = 512;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const imgData = ctx.createImageData(width, height);
  const data = imgData.data;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const nx = x / 50;
      const ny = y / 50;
      const elevation = fractalNoise(nx, ny, 6);
      const crater = Math.sin(fractalNoise(nx * 4, ny * 4, 3) * Math.PI);

      const r = 180 + elevation * 65 - crater * 30;
      const g = 65 + elevation * 40 - crater * 20;
      const b = 35 + elevation * 20 - crater * 15;

      data[idx] = Math.min(Math.max(r, 0), 255);
      data[idx + 1] = Math.min(Math.max(g, 0), 255);
      data[idx + 2] = Math.min(Math.max(b, 0), 255);
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  textureCache.set('mars', texture);
  return texture;
}

/**
 * 4. Jupiter / Banded Gas Giant World Texture
 */
export function getJupiterTexture() {
  if (textureCache.has('jupiter')) return textureCache.get('jupiter');

  const width = 1024;
  const height = 512;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const imgData = ctx.createImageData(width, height);
  const data = imgData.data;

  for (let y = 0; y < height; y++) {
    const latFactor = y / height;
    const band = Math.sin(latFactor * Math.PI * 18 + fractalNoise(latFactor * 10, 0, 3) * 2);

    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const stormNoise = fractalNoise(x / 40, y / 20, 4);

      const spotDx = (x / width - 0.6) * 4;
      const spotDy = (y / height - 0.65) * 8;
      const spotDist = spotDx * spotDx + spotDy * spotDy;
      const isSpot = spotDist < 0.08;

      let r, g, b;
      if (isSpot) {
        r = 210 + stormNoise * 35;
        g = 70 + stormNoise * 20;
        b = 40;
      } else if (band > 0.2) {
        r = 220 + stormNoise * 25;
        g = 180 + stormNoise * 20;
        b = 135 + stormNoise * 15;
      } else if (band > -0.3) {
        r = 175 + stormNoise * 30;
        g = 110 + stormNoise * 20;
        b = 70 + stormNoise * 10;
      } else {
        r = 130 + stormNoise * 25;
        g = 75 + stormNoise * 15;
        b = 45 + stormNoise * 10;
      }

      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  textureCache.set('jupiter', texture);
  return texture;
}

/**
 * 5. High-Fidelity Saturn Concentric Radial Rings Texture
 */
export function getRingTexture() {
  if (textureCache.has('rings')) return textureCache.get('rings');

  const size = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;
  const cy = size / 2;
  const maxR = size / 2;

  // Concentric Radial Gradient with Cassini Division & Multi-ring bands
  const gradient = ctx.createRadialGradient(cx, cy, maxR * 0.44, cx, cy, maxR * 0.98);
  gradient.addColorStop(0.0, 'rgba(0,0,0,0)');
  gradient.addColorStop(0.02, 'rgba(180, 150, 110, 0.25)'); // Inner C Ring
  gradient.addColorStop(0.16, 'rgba(215, 185, 135, 0.75)');
  gradient.addColorStop(0.26, 'rgba(240, 210, 160, 0.95)'); // Main B Ring (Bright & dense)
  gradient.addColorStop(0.54, 'rgba(248, 225, 180, 0.95)');
  gradient.addColorStop(0.58, 'rgba(20, 15, 10, 0.05)');   // Cassini Division (Dark gap)
  gradient.addColorStop(0.64, 'rgba(220, 190, 145, 0.85)'); // A Ring
  gradient.addColorStop(0.82, 'rgba(200, 170, 125, 0.6)');  // Encke division outer zone
  gradient.addColorStop(0.90, 'rgba(160, 130, 90, 0.2)');   // Outer F Ring
  gradient.addColorStop(1.0, 'rgba(0,0,0,0)');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  textureCache.set('rings', texture);
  return texture;
}

/**
 * 6. Neptune / Ice Crystal Giant Texture
 */
export function getNeptuneTexture() {
  if (textureCache.has('neptune')) return textureCache.get('neptune');

  const width = 1024;
  const height = 512;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const imgData = ctx.createImageData(width, height);
  const data = imgData.data;

  for (let y = 0; y < height; y++) {
    const lat = y / height;
    const band = Math.sin(lat * Math.PI * 12 + fractalNoise(lat * 8, 0, 3));

    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const swirl = fractalNoise(x / 40, y / 30, 4);

      const r = 20 + swirl * 40;
      const g = 90 + band * 30 + swirl * 60;
      const b = 210 + band * 25 + swirl * 35;

      data[idx] = Math.min(Math.max(r, 0), 255);
      data[idx + 1] = Math.min(Math.max(g, 0), 255);
      data[idx + 2] = Math.min(Math.max(b, 0), 255);
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  textureCache.set('neptune', texture);
  return texture;
}

/**
 * 7. Volcanic / Molten Lava World Texture
 */
export function getVolcanicTexture() {
  if (textureCache.has('volcanic')) return textureCache.get('volcanic');

  const width = 1024;
  const height = 512;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const imgData = ctx.createImageData(width, height);
  const data = imgData.data;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const nx = x / 40;
      const ny = y / 40;
      const lavaFracture = Math.abs(fractalNoise(nx, ny, 5) - 0.5) * 2;

      if (lavaFracture < 0.12) {
        const heat = (0.12 - lavaFracture) / 0.12;
        data[idx] = 255;
        data[idx + 1] = 120 + heat * 130;
        data[idx + 2] = heat * 50;
      } else {
        const crust = fractalNoise(nx * 2, ny * 2, 4);
        data[idx] = 30 + crust * 30;
        data[idx + 1] = 20 + crust * 15;
        data[idx + 2] = 20 + crust * 15;
      }
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  textureCache.set('volcanic', texture);
  return texture;
}

/**
 * 8. Cybernetic / Dyson Nexus World Texture
 */
export function getCyberTexture() {
  if (textureCache.has('cyber')) return textureCache.get('cyber');

  const width = 1024;
  const height = 512;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const imgData = ctx.createImageData(width, height);
  const data = imgData.data;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const gridX = x % 32;
      const gridY = y % 32;
      const isGrid = gridX === 0 || gridY === 0;
      const isNode = (x % 64 === 0) && (y % 64 === 0);

      if (isNode) {
        data[idx] = 190;
        data[idx + 1] = 100;
        data[idx + 2] = 255;
      } else if (isGrid && Math.random() > 0.4) {
        data[idx] = 80;
        data[idx + 1] = 40;
        data[idx + 2] = 160;
      } else {
        const plate = ((Math.floor(x / 32) + Math.floor(y / 32)) % 2 === 0) ? 25 : 18;
        data[idx] = plate;
        data[idx + 1] = plate + 4;
        data[idx + 2] = plate + 12;
      }
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  textureCache.set('cyber', texture);
  return texture;
}

/**
 * Master texture selector helper
 */
export function getPlanetTextureByType(type) {
  switch (type) {
    case 'earth':
      return getEarthTexture();
    case 'mars':
      return getMarsTexture();
    case 'jupiter':
      return getJupiterTexture();
    case 'neptune':
      return getNeptuneTexture();
    case 'volcanic':
      return getVolcanicTexture();
    case 'cyber':
      return getCyberTexture();
    default:
      return getEarthTexture();
  }
}

/**
 * Pre-warm all textures in the cache for instant zero-jank rendering
 */
export function prewarmCosmicTextureCache(onProgress) {
  const types = ['earth', 'clouds', 'mars', 'jupiter', 'rings', 'neptune', 'volcanic', 'cyber'];
  types.forEach((t, idx) => {
    if (t === 'clouds') getCloudTexture();
    else if (t === 'rings') getRingTexture();
    else getPlanetTextureByType(t);
    if (onProgress) {
      onProgress(Math.round(((idx + 1) / types.length) * 100));
    }
  });
}
